/**
 * Labor Category Resource Hooks
 *
 */

import type { paths } from '@access-cms/api-types';
import { ResourceType } from '@access-cms/shared-constants';

import type { ApiRequestError } from '../../client';
import { queryKeys } from '../../queryKeys';
import type { QueryParamRecord } from '../../types';
import { useApiCreate, useApiDelete, useApiUpdate } from '../useApiMutation';
import { type RefetchInterval, useApiGet, useApiList } from '../useApiQuery';

// =============================================================================
// Type Aliases
// =============================================================================

type LaborCategoryListResponse =
  paths['/api/v1/contracts/{contractId}/labor-categories']['get']['responses']['200']['content']['application/json'];
type LaborCategoryResponse =
  paths['/api/v1/contracts/{contractId}/labor-categories/{id}']['get']['responses']['200']['content']['application/json'];
type LaborCategoryCreateBody =
  paths['/api/v1/contracts/{contractId}/labor-categories']['post']['requestBody']['content']['application/json'];
type LaborCategoryCreateResponse =
  paths['/api/v1/contracts/{contractId}/labor-categories']['post']['responses']['201']['content']['application/json'];
type LaborCategoryUpdateBody = NonNullable<
  paths['/api/v1/contracts/{contractId}/labor-categories/{id}']['patch']['requestBody']
>['content']['application/json'];

type LaborCategory = NonNullable<LaborCategoryResponse['data']>;
type LaborCategoryListFilters = paths['/api/v1/contracts/{contractId}/labor-categories']['get']['parameters']['query'];

// =============================================================================
// Query Hooks
// =============================================================================

interface UseLaborCategoriesQueryOptions {
  enabled?: boolean;
  filters?: LaborCategoryListFilters;
  isActive?: boolean;
  refetchInterval?: RefetchInterval<LaborCategoryListResponse, ApiRequestError>;
}

/**
 * Fetch labor categories for a contract
 */
export function useLaborCategoriesQuery(contractId: string, options?: UseLaborCategoriesQueryOptions) {
  const { enabled, filters, isActive, refetchInterval } = options ?? {};

  const queryParams: QueryParamRecord = {
    ...(filters as QueryParamRecord | undefined),
    ...(isActive !== undefined ? { isActive } : {}),
  };

  return useApiList('/api/v1/contracts/{contractId}/labor-categories', {
    enabled: enabled ?? !!contractId,
    pathParams: { contractId },
    queryKey: queryKeys[ResourceType.CONTRACTS].nested(contractId).laborCategories.list(queryParams),
    queryParams,
    refetchInterval,
  });
}

interface UseLaborCategoryQueryOptions {
  enabled?: boolean;
}

/**
 * Fetch a single labor category by ID for a contract
 */
export function useLaborCategoryQuery(contractId: string, id: string, options?: UseLaborCategoryQueryOptions) {
  return useApiGet(
    '/api/v1/contracts/{contractId}/labor-categories/{id}',
    { contractId, id },
    {
      enabled: options?.enabled ?? !!(contractId && id),
      queryKey: queryKeys[ResourceType.CONTRACTS].nested(contractId).laborCategories.detail(id),
    },
  );
}

// =============================================================================
// Mutation Hooks
// =============================================================================

interface UseCreateLaborCategoryOptions {
  onError?: (error: Error) => void;
  onSuccess?: (response: LaborCategoryCreateResponse) => void;
}

/**
 * Create a labor category for a contract
 */
export function useCreateLaborCategory(contractId: string, options?: UseCreateLaborCategoryOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/labor-categories',
    pathParams: { contractId },
  });
}

interface UseUpdateLaborCategoryOptions {
  onError?: (error: Error) => void;
  onSuccess?: (response: LaborCategoryResponse) => void;
}

/**
 * Update a labor category for a contract
 */
export function useUpdateLaborCategory(contractId: string, options?: UseUpdateLaborCategoryOptions) {
  return useApiUpdate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/labor-categories/{id}',
    pathParams: { contractId },
  });
}

interface UseDeleteLaborCategoryOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Soft delete (deactivate) a labor category for a contract
 */
export function useDeleteLaborCategory(contractId: string, options?: UseDeleteLaborCategoryOptions) {
  return useApiDelete({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/labor-categories/{id}',
    pathParams: { contractId },
  });
}

// =============================================================================
// Exports
// =============================================================================

export type {
  LaborCategory,
  LaborCategoryCreateBody,
  LaborCategoryCreateResponse,
  LaborCategoryResponse,
  LaborCategoryUpdateBody,
  LaborCategoryListFilters,
  LaborCategoryListResponse,
};
